import Ability from 'boot/abilities';
import { defineStore } from 'pinia';
import { Loading } from 'quasar';
import { axiosApi, setApiToken } from 'src/utils/axios';
import { usePushSubscription } from 'src/composables/usePushSubscription';
import { showErrorNotification, showSuccessNotification } from 'src/functions/function-show-notifications';
import { qLocalStorage } from 'src/functions/qLocalStorage.js';
import { Router } from 'src/router/index';
import { useAppSettingsStore } from 'src/stores/useAppSettingsStore';
import { useGlobalStore } from 'src/stores/useGlobalStore';
import { useHoraireStore } from 'src/stores/useHoraireStore';
import { useOnlineStore } from 'src/stores/useOnlineStore';
export const useUserStore = defineStore(
  'User', 
  {
    state: () => ({ loggedIn: false, permissions: [], details: {}, token: null }),
    actions: { 
      login(payload) {
        Loading.show();
        axiosApi
          .post('sanctum/token', {
            identity: payload.email,
            password: payload.password 
          })
          .then(response => {
            this.token = response.data.token;
            setApiToken(axiosApi.defaults);
            this.loggedIn = true;
            this.details = response.data.user;
            Ability.update(response.data.user.casl);
            showSuccessNotification('Connexion réussie!');
            Router.push('/');
          })
          .catch(error => {
            showErrorNotification('Identification échouée.');
          })
          .finally(() => {
            Loading.hide();
          });
      },
      regenUserData(payload) {
        return new Promise((resolve, reject) => {
          const appSettings = useAppSettingsStore();
          const Horaire = useHoraireStore();
          if (appSettings.employeMode.active === true) {
            Horaire.dayPunch = null;
          }
          axiosApi
            .get(`user/get${appSettings.employeModeId ? `/${appSettings.employeModeId}` : ''}`)
            .then(response => {
              this.details = response.data.user;

              Ability.update(response.data.user.casl);
              if (payload) {
                showSuccessNotification('Données utilisateur rechargeés depuis le serveur.');
              }
              //   dispatch('global/updateVirtualDate', null, { root: true });
              resolve();
            })
            .catch(error => {
              console.warn(error);
              reject('Impossible de se connecter au serveur');
            });
        });
      },
      forgot(payload) {
        //Loading.show();

        axiosApi
          .post('forgot', {
            identity: payload.email
          })
          .then(response => {
            if (response.data.data == 'passwords.sent') {
              showSuccessNotification(
                'Un courriel de récupération vous as été envoyés, suivez le lien dans ce courriel afin de changer votre mot de passe.'
              );
            } else {
              showErrorNotification('Aucun compte ne semble associé à ce courriel.');
            }
          }) 
          .catch(() => { 
            showErrorNotification("Une erreur s'est produite");
          })
          .finally(() => { 
            Loading.hide();
            Router.replace('/login'); 
          }); 
      }, 
      logout(payload) {
        const reset = () => {
          this.loggedIn = false;
          this.details = {};
          // eslint-disable-next-line scanjs-rules/identifier_localStorage

          //clear localstorage in 1000ms
          setTimeout(() => {
            qLocalStorage.clear();
            Router.replace('/login');
            window.location.reload();
          }, 1000);
        };

        Loading.show();
        // On a shared device the browser must stop receiving this user's
        // anomaly push notifications after logout.
        usePushSubscription()
          .unsubscribe()
          .finally(() =>
            axiosApi
              .post('logout')
              .then(() => {
                reset();
              })
              .catch(() => {
                showErrorNotification('Session expirée.');
                reset();
              })
          );

        Loading.hide();
      },
      initUser(reloadMsgFn = null) {
        const OnlineStore = useOnlineStore();
        const GlobalStore = useGlobalStore();
        if (this.details.id) {
          if (this.details.casl) {
            Ability.update(this.details.casl);
          }
          // Idempotent, and identity-aware: signing in without a reload has to
          // move the private-channel listeners to the new user.
          OnlineStore.init();
          GlobalStore.syncAppData();
        }
      }
    },
    getters: {
      fullname: state => `${state.details.prenom} ${state.details.nom}`,
      isIdentified: state => state.loggedIn && state.details && state.details.id,
      usergroup: state => state.details.usergroup,
      currentUser: state => state.details,
      isDev: state => state.details.usergroup == 1 || process.env.ENVIRONMENT == 'development',
      isDirecteur: state => [1, 3].includes(state.details.usergroup),
      isInspecteurOrHigher: state => [1, 3, 6].includes(state.details.usergroup),
      isSuperviseur: state => state.details.usergroup == 5,
      isInspecteur: state => state.details.usergroup == 6, 
      isCoach: state => state.details.usergroup == 4,
      isEmploye: state => state.details.usergroup == 2,
      // Per-account "Joignable en cas de problème": punch-anomaly coach
      // standing without the coach role. Read it alongside the usergroup,
      // never instead of it.
      isContactUrgence: state => !!state.details.contact_urgence,
      isNotRegularEmploye: state => [1, 3, 4, 5, 6].includes(state.details.usergroup)
    },
    persist: true
  },
  { persist: true }
);
 